import React, { useEffect, useState } from 'react';
import { Table, Card, Row, Col, Statistic, Tag, Button, Divider } from 'antd';
import { UserOutlined, DatabaseOutlined, PhoneOutlined, MessageOutlined, RiseOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { batchApi, userApi, analyticsApi } from '../../services/api';
import type { Batch, User } from '../../types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const Dashboard: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [summaryStats, setSummaryStats] = useState<any>(null);
  const [messagesByDay, setMessagesByDay] = useState<any[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [batchesData, usersData, summaryData, dayData] = await Promise.all([
        batchApi.list(),
        userApi.list(),
        analyticsApi.getSummaryStats(),
        analyticsApi.getMessagesByDay({ days: 7 }),
      ]);
      setBatches(batchesData);
      setUsers(usersData);
      setSummaryStats(summaryData);
      setMessagesByDay(dayData.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalNumbers = (batches || []).reduce((sum, batch) => sum + (batch?.numbers?.length || 0), 0);

  const userColumns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag color={role === 'admin' ? 'gold' : 'blue'}>{role.toUpperCase()}</Tag>
      ),
    },
    {
      title: 'Assigned Batches',
      dataIndex: 'assignedBatches',
      key: 'assignedBatches',
      render: (batchIds: string[]) => {
        if (!batchIds || batchIds.length === 0) return '-';
        const batchNames = batchIds.map(id => {
          const batch = (batches || []).find(b => b.id === id);
          return batch?.name || 'Unknown';
        });
        return batchNames.join(', ');
      },
    },
    {
      title: 'Numbers',
      key: 'numbers',
      render: (record: User) => {
        if (record.role === 'admin') return 'All';
        if (!record.assignedBatches || record.assignedBatches.length === 0) return '0';
        const count = record.assignedBatches.reduce((sum, batchId) => {
          const batch = (batches || []).find(b => b.id === batchId);
          return sum + (batch?.numbers.length || 0);
        }, 0);
        return count;
      },
    },
    {
      title: 'Last Login',
      dataIndex: 'lastLogin',
      key: 'lastLogin',
      render: (date: string) => date ? dayjs(date).fromNow() : 'Never',
    },
  ];

  const batchColumns = [
    {
      title: 'Batch Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
    },
    {
      title: 'Numbers',
      dataIndex: 'numbers',
      key: 'numbers',
      render: (numbers: string[]) => numbers?.length || 0,
    },
    {
      title: 'Users with Access',
      key: 'users',
      render: (record: Batch) => {
        const usersWithAccess = (users || []).filter(u =>
          u.role === 'admin' || u.assignedBatches?.includes(record.id)
        );
        if (usersWithAccess.length === 0) return '-';
        return usersWithAccess.map(u => u.username).join(', ');
      },
    },
    {
      title: 'Last Sync',
      dataIndex: 'lastSyncAt',
      key: 'lastSyncAt',
      render: (date: string) => date ? dayjs(date).fromNow() : 'Never',
    },
  ];

  return (
    <div>
      <h1>Admin Dashboard</h1>

      {/* Analytics Helicopter View */}
      {summaryStats && (
        <>
          <Card title="Message Analytics Overview (Last 7 Days)" style={{ marginBottom: 24 }}>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col xs={24} sm={12} md={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Total Messages"
                    value={summaryStats.total_messages_7d}
                    prefix={<MessageOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Messages Today"
                    value={summaryStats.messages_today}
                    prefix={<RiseOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Top Batch"
                    value={summaryStats.top_batch?.count || 0}
                    suffix={`msgs`}
                    valueStyle={{ color: '#fa8c16' }}
                  />
                  <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                    {summaryStats.top_batch?.batch_id || 'N/A'}
                  </div>
                </Card>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Card bordered={false}>
                  <Statistic
                    title="Top Country"
                    value={summaryStats.top_country?.count || 0}
                    suffix={`msgs`}
                    valueStyle={{ color: '#722ed1' }}
                  />
                  <div style={{ fontSize: 12, color: '#888', marginTop: 8 }}>
                    {summaryStats.top_country?.country || 'N/A'}
                  </div>
                </Card>
              </Col>
            </Row>

            {/* Mini Chart */}
            <div style={{ marginTop: 16 }}>
              <h4 style={{ marginBottom: 8 }}>Messages Per Day</h4>
              <ResponsiveContainer width="100%" height={150}>
                <LineChart data={messagesByDay}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" style={{ fontSize: 12 }} />
                  <YAxis style={{ fontSize: 12 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#1890ff" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <Button type="primary" onClick={() => navigate('/admin/analytics')}>
                View Detailed Analytics
              </Button>
            </div>
          </Card>
        </>
      )}

      <Divider orientation="left">System Overview</Divider>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Users"
              value={(users || []).length}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Batches"
              value={(batches || []).length}
              prefix={<DatabaseOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic
              title="Total Numbers"
              value={totalNumbers}
              prefix={<PhoneOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={`Users (${(users || []).length} total)`}
        style={{ marginBottom: 24 }}
      >
        <Table
          columns={userColumns}
          dataSource={users}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} users`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Card>

      <Card
        title={`Batches (${(batches || []).length} total)`}
      >
        <Table
          columns={batchColumns}
          dataSource={batches}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} batches`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
        />
      </Card>
    </div>
  );
};

export default Dashboard;
